import type {
  ChatSourceFssFile,
  ChatSourceFssFolder,
} from "@mirage/mosaics/Chat/types";
import { useScopedSources } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useManuallyAddedSources";
import { parseFileExtension } from "@mirage/service-conversation/utils/convertFssEntityToChatSource";
import { transcribeFiles } from "@mirage/service-fss-bridge";
import {
  useActionEvents,
  useConversationEvents,
} from "@mirage/service-fss-bridge/hooks/bridgeObservers";
import type {
  FSSFile,
  TranscribeFileStatus,
  TranscribeFileStatusError,
} from "@mirage/service-fss-bridge/service/bridge_types";
import { ConversationSource } from "@mirage/service-fss-bridge/service/bridge_types";
import { useEffect, useMemo, useState } from "react";

const SUPPORTED_VIDEO_INPUT_EXTENSIONS = [
  "mov",
  "mp4",
  "m4v",
  "avi",
  "mkv",
  "mpeg",
  "mpg",
];
const SUPPORTED_AUDIO_INPUT_EXTENSIONS = [
  "aac",
  "aif",
  "aiff",
  "flac",
  "m4a",
  "m4r",
  "mp3",
  "oga",
  "ogg",
  "wav",
  "wma",
];

export const isTranscribableFile = (file: FSSFile) => {
  const extension = parseFileExtension(file.name);
  return (
    extension &&
    [
      ...SUPPORTED_VIDEO_INPUT_EXTENSIONS,
      ...SUPPORTED_AUDIO_INPUT_EXTENSIONS,
    ].includes(extension.toLowerCase())
  );
};

export const useTranscribeFilesStatus = (
  sources: (ChatSourceFssFile | ChatSourceFssFolder)[],
): {
  inProgress: number;
  fileName?: string;
  errors?: TranscribeFileStatusError[];
} => {
  const actionEvent = useActionEvents("transcribe.files.response");
  const { scopedSources } = useScopedSources();

  const [, setTranscribingStatuses] = useState<
    Record<string, TranscribeFileStatus>
  >({});
  const [inProgress, setInProgress] = useState(0);
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [errors, setErrors] = useState<TranscribeFileStatusError[] | undefined>(
    undefined,
  );

  useEffect(() => {
    if (actionEvent && actionEvent.data) {
      const sourceIds = [...sources, ...scopedSources]
        .filter((source) => source.type === "fss_file")
        .map((source) => source.fileId);
      const fileIdToTranscriptStatus =
        actionEvent.data.fileIdToTranscriptStatus;

      setTranscribingStatuses((prevTranscribingStatuses) => {
        const joinedStatuses = {
          ...prevTranscribingStatuses,
          ...fileIdToTranscriptStatus,
        };

        // We only care about the status of files that are currently added as conversation sources
        const newTranscribingStatuses = Object.fromEntries(
          Object.entries(joinedStatuses).filter(
            ([fileId, status]) =>
              sourceIds.includes(fileId) && status.status !== "completed",
          ),
        );

        const newInProgress = Object.keys(newTranscribingStatuses).length;
        setInProgress(newInProgress);

        if (newInProgress === 1) {
          setFileName(
            Object.values(newTranscribingStatuses).find(
              (status) => status.status !== "completed",
            )?.file.name,
          );
        } else {
          setFileName(undefined);
        }

        const errors = Object.values(newTranscribingStatuses).filter(
          (status) => status.status === "error",
        );

        setErrors(errors.length > 0 ? errors : undefined);

        return newTranscribingStatuses;
      });
    }
  }, [actionEvent, sources, scopedSources]);

  return useMemo(
    () => ({
      inProgress,
      fileName,
      errors,
    }),
    [inProgress, fileName, errors],
  );
};

export const useTranscribeFiles = () => {
  const actionEvent = useActionEvents("add.source");
  const conversationEvent = useConversationEvents();

  useEffect(() => {
    if (actionEvent && actionEvent.data) {
      switch (actionEvent.source) {
        case "add.source":
          actionEvent.data.sources.forEach((source) => {
            if (source.type === "file" && isTranscribableFile(source)) {
              transcribeFiles([source]);
            }
          });
          break;
        default:
          break;
      }
    }
  }, [actionEvent]);

  useEffect(() => {
    if (conversationEvent && conversationEvent.data) {
      switch (conversationEvent.source) {
        case ConversationSource.FssFile:
        case ConversationSource.FssStack: {
          const files =
            conversationEvent.data.files?.filter(isTranscribableFile);
          if (files && files.length > 0) {
            transcribeFiles(files);
          }
          break;
        }
        default:
          break;
      }
    }
  }, [conversationEvent]);
};
